import { TradingABNReports } from '@features/admin/trading/components/abn-reports/TradingABNReports';
import { TradingOnyxBook } from '@features/admin/trading/components/onyx-books/TradingOnyxBook';
import { TradingOrders } from '@features/admin/trading/components/orders/TradingOrders';
import { TradingOtc } from '@features/admin/trading/components/otc/TradingOtc';
import { TradingExecutions } from '@features/admin/trading/components/TradingExecutions';
import { Paper, Stack, Tab, Tabs, useMediaQuery } from '@mui/material';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { usePageTitle } from '@shared/hooks/usePageTitle';
import theme from '@shared/themes/darkTheme';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import NotFoundPage from './NotFoundPage';
import { TradingVolume } from '@features/admin/trading/components/volume/TradingVolume';

type Tab = { label: string; value: string; Component: React.ReactNode };
const TradingAdminTabs: Tab[] = [
  { label: 'Orders', value: 'orders', Component: <TradingOrders /> },
  { label: 'Executions', value: 'executions', Component: <TradingExecutions /> },
  { label: 'Volume', value: 'volume', Component: <TradingVolume /> },
  { label: 'ABN Reports', value: 'abn-reports', Component: <TradingABNReports /> },
  { label: 'OTC', value: 'otc', Component: <TradingOtc /> },
  { label: 'Onyx Books', value: 'onyx-books', Component: <TradingOnyxBook /> },
];

const TabLink = (props: any) => {
  return <Link {...props} to={props.href} />;
};

export const TradingAdminPage = () => {
  usePageTitle('Trading Admin');

  const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const user = useUserContext();
  const { activeTab: tabInURL } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<Tab | undefined>();

  useEffect(() => {
    if (!tabInURL && selectedTab) {
      const params = searchParams.toString();
      navigate(`/admin/trading/${selectedTab.value}${params ? `?${params}` : ''}`);
    } else if (!tabInURL && !selectedTab) {
      setSelectedTab(TradingAdminTabs[0]);
    } else if (tabInURL && !selectedTab) {
      const tab = TradingAdminTabs.find(tab => tab.value === tabInURL);
      if (tab) {
        setSelectedTab(tab);
      }
    } else if (tabInURL && selectedTab && tabInURL !== selectedTab.value) {
      const tab = TradingAdminTabs.find(tab => tab.value === tabInURL);
      if (tab) {
        setSelectedTab(tab);
      }
    }
  }, [tabInURL, selectedTab, searchParams]);

  if (!user?.is_trading_admin) return <NotFoundPage />;

  return selectedTab ? (
    <Stack display="flex" flexDirection="column" flex={1} gap={1} padding={2}>
      <Stack display="flex" flexDirection="row" gap={1}>
        <Paper style={{ flex: 1, maxWidth: isMdScreen ? 'calc(100vw - 272px)' : 'calc(100vw - 106px)' }}>
          <Tabs value={selectedTab.value} variant="scrollable">
            {TradingAdminTabs.map(tab => (
              <Tab
                href={`/admin/trading/${tab.value}`}
                LinkComponent={TabLink}
                key={tab.value}
                label={tab.label}
                value={tab.value}
                style={{ fontSize: 11 }}
              />
            ))}
          </Tabs>
        </Paper>
      </Stack>
      <Paper style={{ display: 'flex', flex: 1 }}>{selectedTab.Component}</Paper>
    </Stack>
  ) : null;
};
